import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { ACTION_LABELS, ACTION_REASONS, VERIFICATION_TYPES, canDo, nextStep, stepLink, stepsFor } from '@/utils/verifications';
import VerificationSteps from './VerificationSteps';
import '@/styles/verification.css';

/**
 * Fenêtre ouverte au clic sur une action verrouillée (« Accepter », « Nouveau trajet »…) : pourquoi, toutes les étapes,
 * « Plus tard » et la prochaine étape. Le bouton reste cliquable : l'utilisateur comprend au lieu de recevoir un refus.
 */
export default function VerificationDialog({ open, onClose, user, action, goal }) {
  const location = useLocation();
  const closeRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open || !user) return null;

  const steps = stepsFor(user, action);
  const next = nextStep(steps);
  const from = `${location.pathname}${location.search || ''}`;
  const label = goal || ACTION_LABELS[action] || ACTION_LABELS.publish;
  const avant = /^[aeéèêiouhy]/i.test(label) ? `Avant d’${label}` : `Avant de ${label}`;
  return (
    <div className="vg vg-overlay" onClick={onClose} data-testid="verification-dialog">
      <div className="vg-dialog" role="dialog" aria-modal="true" aria-labelledby="vg-dialog-title" onClick={(e) => e.stopPropagation()}>
        <div className="vg-dialog-head">
          <h2 className="vg-dialog-title" id="vg-dialog-title">{avant}</h2>
          <button type="button" className="vg-close" onClick={onClose} aria-label="Fermer" ref={closeRef}><X size={18} /></button>
        </div>
        <p className="vg-reason">{ACTION_REASONS[action]}</p>
        <VerificationSteps steps={steps} from={from} actions={false} />
        <div className="vg-actions">
          <button type="button" className="vg-btn vg-btn--ghost" onClick={onClose} data-testid="verification-dialog-later">Plus tard</button>
          {next && (
            <Link to={stepLink(next.kind, from)} className="vg-btn vg-btn--primary" data-testid="verification-dialog-next">
              {next.state === 'REJECTED' ? 'Renvoyer ma pièce' : VERIFICATION_TYPES[next.kind]?.cta || 'Vérifier'}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Pour un bouton dont l'action exige des vérifications : `run(fn)` lance l'action si le compte est prêt, sinon ouvre
 * la fenêtre. `dialog` est l'élément à placer dans la page.
 */
export function useVerifiedAction(user, action, goal) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const run = useCallback((fn) => {
    if (canDo(user, action)) return fn();
    setOpen(true);
    return undefined;
  }, [user, action]);
  const dialog = <VerificationDialog open={open} onClose={close} user={user} action={action} goal={goal} />;
  return { run, dialog, locked: !!user && !canDo(user, action) };
}
